import LandingPageBackground from "components/utils/LandingPageBackground";
import { usePlatform } from "components/utils/PlatformContext";
import { useEffect, useState } from "react";
import { MdOutlineQrCode } from "react-icons/md";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  sendNewCode2FARequest,
  twoFactorCheckId,
  twoFactorValidateCode,
} from "services/authRequests";
import { clockify } from "utils/utils";

const TwoFactorAuthentication = () => {
  // Paramters definition
  const defaultTimer = 180; // Seconds
  const codeLength = 6; // Numbers
  const loginUrl = "/";

  // States definition
  const [email, setEmail] = useState("");
  const [expirationTime, setExpirationTime] = useState("");
  const [countDown, setCountDown] = useState<number>(defaultTimer);
  const [showError, setShowError] = useState<boolean>(false);
  const [inputDisabled, setInputDisabled] = useState<boolean>(false);
  const [codeAttemptLimitError, setCodeAttemptLimitError] = useState(false);
  const [requestAttemptLimitError, setRequestAttemptLimitError] =
    useState(false);
  const [codeAttempt, setCodeAttempt] = useState(0);

  // Get the platform context
  const { logo, limitCodeAttempts } = usePlatform();

  // Navigate definition
  const navigate = useNavigate();
  // Use the location to avoid enter this page from any other place
  const { state } = useLocation();

  // Get the twoFactorId
  const { twoFactorId } = useParams();

  /**
   * Check if the page is accessed by a redirection
   */
  if (!state?.fromApp) {
    navigate("*");
  }

  /**
   * Check if the twoFactorAuthenticationId is valid, if not send again to login
   */
  useEffect(() => {
    twoFactorCheckId(twoFactorId || "").then((response) => {
      // If the response it's not successful, send to login again
      if (!response.successful) {
        navigate(loginUrl);
      }

      // Set the email of the reset token
      setEmail(response.email);
      setExpirationTime(response.expiration);
    });
  }, []);

  /**
   * Function to update the countdown value based on the expiration time
   * of the two factor authentication record in the database
   */
  useEffect(() => {
    if (expirationTime) {
      // Transform the expiration time into a date
      const targetDate = new Date(expirationTime);
      // Get the current time
      const now = new Date();

      // Get the difference of time
      const diffInMilliseconds = targetDate.getTime() - now.getTime();
      // And transform it into seconds
      const diffInSeconds = Math.floor(diffInMilliseconds / 1000);

      // Set the new countdown
      setCountDown(diffInSeconds);
    }
  }, [expirationTime]);

  /**
   * Counterdown routine
   */
  useEffect(() => {
    // Just perform the countdown while the timer is greater than zero
    if (countDown > 0 && !codeAttemptLimitError) {
      const interval = setInterval(() => {
        setCountDown(countDown - 1);
      }, 1000);

      // Clearing the interval
      return () => clearInterval(interval);
    }
  }, [countDown]);

  /**
   * Request attempt timer
   */
  useEffect(() => {
    // Just perform the countdown while the timer is greater than zero
    if (requestAttemptLimitError) {
      const timer = setInterval(() => {
        setRequestAttemptLimitError(false);
      }, 5000);

      // Clearing the interval
      return () => clearInterval(timer);
    }
  }, [requestAttemptLimitError]);

  /**
   * Routine for defining the code attempt limit
   */
  useEffect(() => {
    if (codeAttempt >= limitCodeAttempts) setCodeAttemptLimitError(true);
  }, [codeAttempt]);

  /**
   * Function to send a new code to authenticate
   */
  const sendNewCode = async () => {
    // Send the request
    const response = await sendNewCode2FARequest(twoFactorId, email);

    // If there is any error with this, then try again with the login
    if (!response.successful) {
      // Check if it's due to an attempt limit
      if (response.attemptLimit) {
        setRequestAttemptLimitError(true);
        return;
      } else navigate(loginUrl);
    }

    // And set the timer again
    setCountDown(defaultTimer);
  };

  /**
   * Function to track the code input to check if the input code is right
   */
  const codeInputTracking = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value.length === codeLength && countDown > 0) {
      // Disable the input and the error messages while validating
      setShowError(false);
      setCodeAttemptLimitError(false);
      setRequestAttemptLimitError(false);
      setInputDisabled(true);

      // Check if the input code is valid
      const response = await twoFactorValidateCode(
        twoFactorId || "",
        event.target.value,
        state.remember
      );

      // Check the response from the server
      if (response.successful) {
        navigate("/home");
      } else {
        // Show the error message
        setShowError(true);
        // Increase the number of the attempt
        setCodeAttempt(response.codeAttempt);
      }
    }

    // Return to the regular input behaviour
    setInputDisabled(false);
  };

  return (
    <LandingPageBackground>
      <div className="w-full h-full min-h-screen inline-flex items-center justify-between p-14 z-10 overflow-auto">
        <div className="w-fit" />
        <div className="w-[628px] min-w-[500px] min-h-fit p-24 bg-neutral-100 bg-opacity-70 rounded-2xl shadow backdrop-blur-[32px] justify-center items-start gap-12 inline-flex flex-col">
          <Link
            className="w-fit justify-start items-center gap-2 inline-flex select-none"
            to="/"
          >
            <img className="w-fit h-[91px]" src={logo} alt="Platform logo" />
          </Link>
          <h1 className="self-stretch h-[34px] text-neutral-700 text-[33px] font-semibold font-sans leading-[34px]">
            Two-factor authentication
          </h1>
          <div className="self-stretch h-fit flex-col justify-start items-start gap-8 flex">
            <div className="self-stretch justify-start items-center">
              <span className="text-neutral-700 text-base font-normal font-sans leading-normal">
                We just sent you a verification code to:{" "}
              </span>
              <span className="text-neutral-700 text-base font-bold font-sans leading-normal">
                {email}
              </span>
            </div>
            <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
              <div className="self-stretch justify-start items-start gap-2 inline-flex">
                <div className="grow shrink basis-0 h-[17px] text-neutral-700 text-base font-normal font-sans leading-[17px] select-none">
                  <span>Code expires in </span>
                  <span className="font-bold">
                    {expirationTime && clockify(countDown)}
                  </span>
                </div>
                {!codeAttemptLimitError && (
                  <button
                    className="text-right text-success-700 hover:text-success-800 text-base font-bold font-sans underline leading-[17px] cursor-pointer select-none"
                    onClick={sendNewCode}
                  >
                    Send a new code
                  </button>
                )}
              </div>

              {codeAttemptLimitError ? (
                <div className="text-error-600 text-sm font-normal font-sans">
                  Oops! You've used all your allowed attempts.
                </div>
              ) : (
                <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
                  <label
                    htmlFor="2fa-code"
                    className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
                  >
                    2FA code
                  </label>
                  <div
                    className={`w-full h-[51px] rounded-lg text-neutral-700 text-base font-normal font-sans leading-[19px] flex items-center relative ${
                      inputDisabled ? "bg-neutral-200" : "bg-neutral-50"
                    }`}
                  >
                    <div className="absolute left-3.5 w-5 h-5 z-[5]">
                      <MdOutlineQrCode className="w-full h-full text-neutral-500" />
                    </div>
                    <input
                      id="2fa-code"
                      type="text"
                      className={`w-full h-full pl-[42px] pr-12 rounded-lg bg-transparent placeholder:text-neutral-300 placeholder:select-none focus:outline-none text-base font-normal font-sans leading-[19px] z-10 ${
                        inputDisabled ? "text-neutral-300" : "text-neutral-700"
                      }`}
                      placeholder="Enter the 2FA code sent to your email"
                      onChange={codeInputTracking}
                      // maxLength={codeLength}
                      disabled={inputDisabled}
                    />
                  </div>
                  {showError && !codeAttemptLimitError && (
                    <div className="text-error-600 text-sm font-normal font-sans">
                      Error: The Two-factor authentication code is not matching
                      (attempt {codeAttempt} of {limitCodeAttempts})
                    </div>
                  )}
                  {requestAttemptLimitError && !codeAttemptLimitError && (
                    <div className="text-error-600 text-sm font-normal font-sans">
                      Oops! You've used all your requesting new code attempts.<br/>Please try again once this code is expired.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="self-stretch inline-flex items-center justify-center">
            <Link
              to={loginUrl}
              className="text-center text-success-700 hover:text-success-800 text-base font-bold font-sans underline leading-[17px]"
            >
              Back to Login
            </Link>
          </div>
        </div>
        <div className="w-fit" />
      </div>
    </LandingPageBackground>
  );
};

export default TwoFactorAuthentication;
